import { Alert, Button, HStack, Stack, Text, VStack, Wrap } from '@chakra-ui/react';
import { SessionReviewList } from '@src/components/sco/Advisors/reviews/SessionReviewList';
import { SessionReviewReportDialog } from '@src/components/sco/Advisors/reviews/SessionReviewReportDialog';
import { SessionReviewSummary } from '@src/components/sco/Advisors/reviews/SessionReviewSummary';
import { canFeatureSessionReview, featuredSessionReviewCount, SESSION_REVIEW_FEATURE_LIMIT, sessionReviewErrorMessage, type SessionReviewItem } from '@src/components/sco/Advisors/reviews/sessionReviewHelpers';
import { useState } from 'react';
import { LuEye, LuEyeOff, LuFlag, LuRefreshCw, LuStar, LuStarOff, LuTextCursor } from 'react-icons/lu';
type ReviewAction = 'feature' | 'hide' | 'hideText' | 'report';
export type SessionReviewsManageViewProps = {
  /** Manage-view items (`includeHidden: true`): hidden rows included with `isHidden` / `isHiddenByAdmin`. */
  reviews: SessionReviewItem[];
  summary?: {
    averageScore?: number | null;
    count?: number | null;
  } | null;
  hasMore?: boolean;
  loadingMore?: boolean;
  onShowMore?: () => void;
  onFeature: (review: SessionReviewItem, featured: boolean) => Promise<unknown>;
  onHide: (review: SessionReviewItem, hidden: boolean) => Promise<unknown>;
  onHideText: (review: SessionReviewItem, hidden: boolean) => Promise<unknown>;
  onReport: (review: SessionReviewItem, reason: string) => Promise<unknown>;
  /** Show "Sync from Clarity" (member has a linked Clarity account). */
  canSyncClarity?: boolean;
  /** Resolves to the result line shown under the button (e.g. created/updated/removed counts). */
  onSyncClarity?: () => Promise<string>;
};

/** Reviewee's Reviews tab body: summary, Sync from Clarity, and per-review manage actions (L40). */
export const SessionReviewsManageView = ({
  reviews,
  summary,
  hasMore,
  loadingMore,
  onShowMore,
  onFeature,
  onHide,
  onHideText,
  onReport,
  canSyncClarity = false,
  onSyncClarity
}: SessionReviewsManageViewProps) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [reportTarget, setReportTarget] = useState<SessionReviewItem | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportedIds, setReportedIds] = useState<Record<string, boolean>>({});
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const featuredCount = featuredSessionReviewCount(reviews);
  const run = async (review: SessionReviewItem, action: ReviewAction, task: () => Promise<unknown>) => {
    setBusy(`${review.id}:${action}`);
    setRowErrors(current => {
      const next = {
        ...current
      };
      delete next[review.id];
      return next;
    });
    try {
      await task();
    } catch (error) {
      setRowErrors(current_0 => ({
        ...current_0,
        [review.id]: sessionReviewErrorMessage(error, 'Could not update that review.')
      }));
    } finally {
      setBusy(null);
    }
  };
  const handleSync = async () => {
    if (!onSyncClarity) {
      return;
    }
    setSyncing(true);
    setSyncResult(null);
    try {
      setSyncResult({
        ok: true,
        message: await onSyncClarity()
      });
    } catch (error_0) {
      setSyncResult({
        ok: false,
        message: sessionReviewErrorMessage(error_0, 'Clarity sync failed.')
      });
    } finally {
      setSyncing(false);
    }
  };
  const handleReport = async (reason: string) => {
    if (!reportTarget) {
      return;
    }
    const target = reportTarget;
    setReportError(null);
    setBusy(`${target.id}:report`);
    try {
      await onReport(target, reason);
      setReportedIds(current_1 => ({
        ...current_1,
        [target.id]: true
      }));
      setReportTarget(null);
    } catch (error_1) {
      setReportError(sessionReviewErrorMessage(error_1, 'Could not send that report.'));
    } finally {
      setBusy(null);
    }
  };
  const renderActions = (review_0: SessionReviewItem) => {
    const isBusy = (action_0: ReviewAction) => busy === `${review_0.id}:${action_0}`;
    const hidden = !!review_0.isHidden || !!review_0.isHiddenByAdmin;
    const featureAllowed = canFeatureSessionReview(review_0, featuredCount);
    const error_2 = rowErrors[review_0.id];
    return <VStack align={'stretch'} gap={2}>
        <Wrap gap={2}>
          <Button disabled={hidden && !review_0.isFeatured} loading={isBusy('feature')} size={'xs'} variant={'outline'} title={hidden ? 'Hidden reviews cannot be featured.' : !featureAllowed ? `You can feature up to ${SESSION_REVIEW_FEATURE_LIMIT} reviews.` : undefined} onClick={() => void run(review_0, 'feature', () => onFeature(review_0, !review_0.isFeatured))}>
            {review_0.isFeatured ? <LuStarOff /> : <LuStar />}
            {review_0.isFeatured ? 'Unfeature' : 'Feature'}
          </Button>
          <Button loading={isBusy('hide')} size={'xs'} variant={'outline'} onClick={() => void run(review_0, 'hide', () => onHide(review_0, !review_0.isHidden))}>
            {review_0.isHidden ? <LuEye /> : <LuEyeOff />}
            {review_0.isHidden ? 'Unhide review' : 'Hide review'}
          </Button>
          {review_0.text && <Button loading={isBusy('hideText')} size={'xs'} variant={'outline'} onClick={() => void run(review_0, 'hideText', () => onHideText(review_0, !review_0.isTextHidden))}>
              <LuTextCursor />
              {review_0.isTextHidden ? 'Show text' : 'Hide text'}
            </Button>}
          <Button disabled={!!reportedIds[review_0.id]} loading={isBusy('report')} size={'xs'} variant={'ghost'} onClick={() => {
          setReportError(null);
          setReportTarget(review_0);
        }}>
            <LuFlag />
            {reportedIds[review_0.id] ? 'Reported' : 'Report'}
          </Button>
        </Wrap>
        {error_2 && <Text color={'red.fg'} fontSize={'sm'} role={'alert'}>
            {error_2}
          </Text>}
      </VStack>;
  };
  return <VStack align={'stretch'} gap={5} width={'full'}>
      <Stack align={{
      base: 'stretch',
      md: 'center'
    }} direction={{
      base: 'column',
      md: 'row'
    }} gap={3} justify={'space-between'}>
        <VStack align={'flex-start'} gap={1}>
          <SessionReviewSummary averageScore={summary?.averageScore} count={summary?.count} />
          <Text color={'fg.muted'} fontSize={'sm'}>
            Feature up to {SESSION_REVIEW_FEATURE_LIMIT} reviews ({featuredCount} featured). Hidden reviews
            still count toward your rating.
          </Text>
        </VStack>
        {canSyncClarity && onSyncClarity && <HStack flexShrink={0}>
            <Button loading={syncing} size={'sm'} variant={'outline'} onClick={() => void handleSync()}>
              <LuRefreshCw />
              Sync from Clarity
            </Button>
          </HStack>}
      </Stack>

      {syncResult && <Alert.Root size={'sm'} status={syncResult.ok ? 'success' : 'error'}>
          <Alert.Indicator />
          <Alert.Title>{syncResult.message}</Alert.Title>
        </Alert.Root>}

      <SessionReviewList showManageState emptyText={'No reviews yet. Reviews from your sessions show here.'} hasMore={hasMore} loadingMore={loadingMore} renderActions={renderActions} reviews={reviews} onShowMore={onShowMore} />

      <SessionReviewReportDialog error={reportError} open={!!reportTarget} submitting={!!reportTarget && busy === `${reportTarget.id}:report`} onSubmit={handleReport} onOpenChange={open => {
      if (!open) {
        setReportTarget(null);
      }
    }} />
    </VStack>;
};